import { useCallback, useMemo } from 'react';
import { useMutation, type EnsureQueryDataOptions, type QueryKey } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { isMobile } from 'react-device-detect';
import { queryClient } from '../Core/Query/QueryClient';
import { AppSections } from '../Core/State/AppSections';
import { getCurrentLanguage } from '../i18n';
import { pokerGraphqlRequest } from '../Utils/PokerGraphqlClient';
import { casinoGameBySlugQueryOptions, categoriesQueryOptions, getCachedCasinoGameBySlug, providersQueryOptions } from './CasinoQueries';
import type {
  CasinoCategory,
  CasinoFile,
  CasinoGame,
  CasinoGamesFilter,
  CasinoGamesPage,
  CasinoLaunchResult,
  CasinoProvider,
} from './Type/CasinoTypes';
import {
  CASINO_GAMES_QUERY,
  PROVIDERS_QUERY,
  CATEGORIES_QUERY,
  LAUNCH_MUTATION,
} from './GraphQlQuery/CasinoGraphqlQueries';
import { setCasinoLobbyPreset } from './CasinoLobbyPreset';
import { ICasinoGameSession } from './Interface/ICasinoGameSession';

export class CasinoHelper {
  public static async getProviders(args: { slotegratorOnly: boolean; signal?: AbortSignal }): Promise<CasinoProvider[]> {
    const res = await pokerGraphqlRequest<{ casinoProviders: CasinoProvider[] }, { slotegratorOnly: boolean }>({
      query: PROVIDERS_QUERY,
      variables: { slotegratorOnly: args.slotegratorOnly },
      signal: args.signal,
    });
    return (res.casinoProviders || []).slice();
  }

  public static async getCategories(args?: { signal?: AbortSignal }): Promise<CasinoCategory[]> {
    const res = await pokerGraphqlRequest<{ casinoCategories: CasinoCategory[] }>({
      query: CATEGORIES_QUERY,
      signal: args?.signal,
    });
    return (res.casinoCategories || []).slice();
  }

  public static async getGames(args: {
    page: number;
    pageSize: number;
    filter?: CasinoGamesFilter;
    signal?: AbortSignal;
  }): Promise<CasinoGamesPage> {
    const res = await pokerGraphqlRequest<
      { casinoGames: CasinoGamesPage },
      { page: number; pageSize: number; filter?: CasinoGamesFilter }
    >({
      query: CASINO_GAMES_QUERY,
      variables: { page: args.page, pageSize: args.pageSize, filter: args.filter },
      signal: args.signal,
    });
    return res.casinoGames;
  }

  public static async launchGame(args: {
    slug: string;
    demo: boolean;
    language: string;
    isMobile?: boolean;
    signal?: AbortSignal;
  }): Promise<CasinoLaunchResult> {
    const res = await pokerGraphqlRequest<{ launchCasinoGame: CasinoLaunchResult }, { input: Record<string, unknown> }>({
      query: LAUNCH_MUTATION,
      variables: {
        input: {
          slug: args.slug,
          demo: args.demo,
          language: args.language,
          ...(typeof args.isMobile === 'boolean' ? { isMobile: args.isMobile } : {}),
        },
      },
      signal: args.signal,
    });
    return res.launchCasinoGame;
  }
}

export const cleanProviderName = (name: string): string => {
  return name.replace(/^slotegrator\s*/i, '').trim();
};

export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html);
};

export const getPlainTextFromHtml = (html: string): string => {
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  } catch {
    return html;
  }
};

export const getCasinoFileUrl = (file?: CasinoFile | null): string | null => {
  const explicitUrl = file?.url?.trim();
  if (explicitUrl && explicitUrl.length > 0) return explicitUrl;

  const website = file?.website?.trim();
  const filename = file?.filename?.trim();

  if (!filename) return null;

  const maybeUpgradeToHttps = (url: string): string => {
    try {
      if (typeof window === 'undefined') return url;
      if (window.location.protocol !== 'https:') return url;
      const parsed = new URL(url);
      if (parsed.protocol === 'http:') {
        parsed.protocol = 'https:';
        return parsed.toString();
      }
    } catch {}
    return url;
  };

  if (/^https?:\/\//i.test(filename)) return maybeUpgradeToHttps(filename);
  if (!website) {
    return filename.startsWith('/') ? filename : null;
  }

  const sanitizedFilename = filename.replace(/^\/+/, '');
  const joined = website.endsWith('/') ? `${website}${sanitizedFilename}` : `${website}/${sanitizedFilename}`;

  if (/^https?:\/\//i.test(joined)) return maybeUpgradeToHttps(joined);
  if (joined.startsWith('//')) {
    try {
      if (typeof window === 'undefined') return joined;
      return `${window.location.protocol}${joined}`;
    } catch {
      return joined;
    }
  }

  return joined.startsWith('/') ? joined : null;
};

export const getProviderIconUrl = (provider?: CasinoProvider | null): string | null => {
  if (!provider) return null;
  const iconUrl = provider.iconUrl?.trim();
  const imageUrl = provider.imageUrl?.trim();
  if (iconUrl && iconUrl.length > 0) return iconUrl;
  if (imageUrl && imageUrl.length > 0) return imageUrl;
  return getCasinoFileUrl(provider.iconFile) || getCasinoFileUrl(provider.imageFile);
};

export const getCategoryIconUrl = (category?: CasinoCategory | null): string | null => {
  if (!category) return null;
  const iconUrl = category.iconUrl?.trim();
  const imageUrl = category.imageUrl?.trim();
  if (iconUrl && iconUrl.length > 0) return iconUrl;
  if (imageUrl && imageUrl.length > 0) return imageUrl;
  return getCasinoFileUrl(category.iconFile) || getCasinoFileUrl(category.imageFile);
};

export const getProviderById = (providers: CasinoProvider[], providerId?: string): CasinoProvider | null => {
  if (!providerId) return null;
  return providers.find((p) => p.id === providerId) || null;
};

export const getProviderName = (providers: CasinoProvider[], providerId?: string): string | null => {
  const provider = getProviderById(providers, providerId);
  if (!provider?.title) return null;
  return cleanProviderName(provider.title) || null;
};

export const getProviderIconUrlById = (providers: CasinoProvider[], providerId?: string): string | null => {
  const provider = getProviderById(providers, providerId);
  return getProviderIconUrl(provider);
};

export const getGameThumbUrl = (game: CasinoGame): string | null => {
  if (game.image && game.image.length > 0) return game.image;
  const alternative = getCasinoFileUrl(game.alternativeImageFile);
  if (alternative) return alternative;
  return null;
};

export const getAlternativeImageUrl = (game: CasinoGame): string => {
  if (game.alternativeImageFile && game.alternativeImageFile.url && game.alternativeImageFile.url.length > 0) return game.alternativeImageFile.url;
  return getGameThumbUrl(game) || '/images/animated-icon.gif';
};

export const getGameCardImageUrl = (game: CasinoGame): string => {
  return getGameThumbUrl(game) || '/images/animated-icon.gif';
};

export const formatRtp = (rtp?: number | null): string | null => {
  if (rtp === null || rtp === undefined) return null;
  const numeric = Number(rtp);
  if (!Number.isFinite(numeric)) return null;

  const normalized = numeric <= 1 ? numeric * 100 : numeric;
  const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(normalized);
  return `${formatted}%`;
};

export const formatVolatility = (volatility?: string | number | null): string => {
  const volatilityText = ['UNKNOWN', 'LOW', 'MEDIUM LOW', 'MEDIUM', 'MEDIUM HIGH', 'HIGH', 'VARIABLE'];
  
  if (volatility === null || volatility === undefined) return 'UNKNOWN';
  
  if (typeof volatility === 'number') {
    if (!Number.isFinite(volatility) || volatility === 0) return 'UNKNOWN';
    const index = Math.floor(volatility);
    if (index >= 1 && index <= 6) {
      return volatilityText[index];
    }
    return 'UNKNOWN';
  }

  const trimmed = volatility.trim();
  if (trimmed.length === 0) return 'UNKNOWN';
  
  // Check if it's already a valid volatility text
  const upperTrimmed = trimmed.toUpperCase();
  if (volatilityText.includes(upperTrimmed)) {
    return upperTrimmed;
  }
  
  // Try to parse as number
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric) && numeric !== 0) {
    const index = Math.floor(numeric);
    if (index >= 1 && index <= 6) {
      return volatilityText[index];
    }
  }
  
  return 'UNKNOWN';
};

export const buildCasinoGameSession = (args: {
  launch: CasinoLaunchResult;
  slug: string;
  demo: boolean;
  isMobile: boolean;
  game?: CasinoGame | null;
  titleHint?: string;
  imageHint?: string | null;
  providerIdHint?: string;
}): ICasinoGameSession => {
  const url = args.launch?.url;
  if (!url) {
    throw new Error('Missing launch url');
  }

  const launchGameData = args.launch?.game;
  const game = args.game ?? null;

  const gameId = launchGameData?.id || game?.id || args.slug;
  const sessionId = args.launch?.sessionToken || `${gameId}-${Date.now()}`;
  const slug = launchGameData?.slug || args.slug;
  const title = launchGameData?.title || game?.title || args.titleHint || args.slug;

  const imageHint = args.imageHint?.trim();
  const image = imageHint && imageHint.length > 0 ? imageHint : (game ? getGameThumbUrl(game) : null);
  const providerId = args.providerIdHint || game?.providerId;

  return {
    sessionId,
    gameId,
    slug,
    title,
    url,
    demo: args.demo,
    image: image ?? null,
    providerId,
    openedAt: Date.now(),
    isMobile: args.isMobile,
  };
};

export type CasinoLinkAction =
  | {
      type: 'game';
      slug: string;
      title?: string;
      image?: string | null;
      providerId?: string;
      demo?: boolean;
    }
  | {
      type: 'provider';
      id?: string;
      slug?: string;
    }
  | {
      type: 'category';
      id?: string;
      slug?: string;
    };

type BaseExecuteCasinoLinkActionArgs = {
  action: CasinoLinkAction;
  openCasinoGameSession?: (session: ICasinoGameSession) => void;
  navigateToCasinoLobby?: () => void;
  demoOverride?: boolean;
  isMobile?: boolean;
  language?: string;
  game?: CasinoGame | null;
  signal?: AbortSignal;
};

type GameExecuteCasinoLinkActionArgs = BaseExecuteCasinoLinkActionArgs & {
  action: Extract<CasinoLinkAction, { type: 'game' }>;
  openCasinoGameSession: (session: ICasinoGameSession) => void;
  isMobile: boolean;
  language: string;
};

type ProviderExecuteCasinoLinkActionArgs = BaseExecuteCasinoLinkActionArgs & {
  action: Extract<CasinoLinkAction, { type: 'provider' }>;
};

type CategoryExecuteCasinoLinkActionArgs = BaseExecuteCasinoLinkActionArgs & {
  action: Extract<CasinoLinkAction, { type: 'category' }>;
};

type ExecuteCasinoLinkActionArgs =
  | GameExecuteCasinoLinkActionArgs
  | ProviderExecuteCasinoLinkActionArgs
  | CategoryExecuteCasinoLinkActionArgs;

export type CasinoLinkActionDefaults = {
  openCasinoGameSession?: (session: ICasinoGameSession) => void;
  navigateToCasinoLobby?: () => void;
  isMobile?: boolean;
  language?: string;
};

export type CasinoLinkActionRequest = {
  action: CasinoLinkAction;
  openCasinoGameSession?: (session: ICasinoGameSession) => void;
  navigateToCasinoLobby?: () => void;
  demoOverride?: boolean;
  isMobile?: boolean;
  language?: string;
  game?: CasinoGame | null;
  signal?: AbortSignal;
};

type LaunchCasinoGameArgs = {
  slug: string;
  demo: boolean;
  language: string;
  isMobile: boolean;
  signal?: AbortSignal;
};

type LaunchCasinoGameFn = (args: LaunchCasinoGameArgs) => ReturnType<typeof CasinoHelper.launchGame>;

type CasinoInteractionHandlerLike = {
  openCasinoGameSession?: (session: ICasinoGameSession) => void;
  setSection?: (section: string) => void;
};

const getInteractionHandler = (): CasinoInteractionHandlerLike | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as { interactionHandler?: CasinoInteractionHandlerLike }).interactionHandler;
};

const ensureQueryDataWithSignal = async <TQueryFnData, TError, TData, TQueryKey extends QueryKey>(
  options: EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  signal?: AbortSignal
): Promise<TData> => {
  if (!signal) {
    return queryClient.ensureQueryData(options);
  }

  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const abortHandler = () => {
    void queryClient.cancelQueries({ queryKey: options.queryKey });
  };

  signal.addEventListener('abort', abortHandler, { once: true });
  try {
    return await queryClient.ensureQueryData(options);
  } finally {
    signal.removeEventListener('abort', abortHandler);
  }
};

export const createExecuteCasinoLinkAction = (deps: { launchGame: LaunchCasinoGameFn }) => {
  return async (args: ExecuteCasinoLinkActionArgs): Promise<void> => {
    if (args.action.type === 'game') {
      const action = args.action;
      if (!args.openCasinoGameSession) {
        throw new Error('openCasinoGameSession is required for game links');
      }
      if (typeof args.isMobile !== 'boolean') {
        throw new Error('isMobile is required for game links');
      }
      if (!args.language) {
        throw new Error('language is required for game links');
      }

      const demo = typeof args.demoOverride === 'boolean' ? args.demoOverride : action.demo ?? false;
      const isMobileValue = args.isMobile;
      const language = args.language;

      const cachedGame = getCachedCasinoGameBySlug(action.slug, isMobileValue);
      const directGame = args.game ?? null;

      const metaPromise =
        directGame || cachedGame
          ? Promise.resolve(directGame || cachedGame)
          : ensureQueryDataWithSignal(
              casinoGameBySlugQueryOptions({ slug: action.slug, isMobile: isMobileValue, titleHint: action.title }),
              args.signal
            ).catch(() => null);

      const launchPromise = deps.launchGame({
        slug: action.slug,
        demo,
        language,
        isMobile: isMobileValue,
        signal: args.signal,
      });

      const [launch, meta] = await Promise.all([launchPromise, metaPromise]);

      const session = buildCasinoGameSession({
        launch,
        slug: action.slug,
        demo,
        isMobile: isMobileValue,
        game: meta || directGame || cachedGame,
        titleHint: action.title,
        imageHint: action.image ?? null,
        providerIdHint: action.providerId,
      });

      args.openCasinoGameSession(session);
      return;
    }

    if (args.action.type === 'provider') {
      const action = args.action;
      let providerId = action.id;
      if (!providerId && action.slug) {
        const providers = await ensureQueryDataWithSignal(providersQueryOptions(true), args.signal);
        providerId = providers.find((p) => p.slug === action.slug)?.id;
      }
      if (!providerId) throw new Error('Provider not found');

      setCasinoLobbyPreset({ searchInput: '', providerIds: [providerId], categoryIds: [] });
      args.navigateToCasinoLobby?.();
      return;
    }

    if (args.action.type === 'category') {
      const action = args.action;
      let categoryId = action.id;
      if (!categoryId && action.slug) {
        const categories = await ensureQueryDataWithSignal(categoriesQueryOptions(), args.signal);
        categoryId = categories.find((c) => c.slug === action.slug)?.id;
      }
      if (!categoryId) throw new Error('Category not found');

      setCasinoLobbyPreset({ searchInput: '', providerIds: [], categoryIds: [categoryId] });
      args.navigateToCasinoLobby?.();
      return;
    }
  };
};

export function useLaunchCasinoGameMutation() {
  return useMutation<
    CasinoLaunchResult,
    Error,
    { slug: string; demo: boolean; language: string; isMobile: boolean; signal?: AbortSignal }
  >({
    mutationFn: (variables) =>
      CasinoHelper.launchGame({
        slug: variables.slug,
        demo: variables.demo,
        language: variables.language,
        isMobile: variables.isMobile,
        signal: variables.signal,
      }),
  });
}

export function useExecuteCasinoLinkAction() {
  const { mutateAsync: launchCasinoGame } = useLaunchCasinoGameMutation();

  return useMemo(() => createExecuteCasinoLinkAction({ launchGame: launchCasinoGame }), [launchCasinoGame]);
}

export function useCasinoLinkAction(defaults?: CasinoLinkActionDefaults) {
  const executeLinkAction = useExecuteCasinoLinkAction();
  const defaultOpenCasinoGameSession = defaults?.openCasinoGameSession;
  const defaultNavigateToCasinoLobby = defaults?.navigateToCasinoLobby;
  const defaultIsMobile = defaults?.isMobile;
  const defaultLanguage = defaults?.language;

  return useCallback(
    async (args: CasinoLinkActionRequest) => {
      const interactionHandler = getInteractionHandler();
      const openCasinoGameSession =
        args.openCasinoGameSession ?? defaultOpenCasinoGameSession ?? interactionHandler?.openCasinoGameSession;
      const navigateToCasinoLobby =
        args.navigateToCasinoLobby ??
        defaultNavigateToCasinoLobby ??
        (interactionHandler?.setSection ? () => interactionHandler.setSection?.(AppSections.CASINO) : undefined);

      if (args.action.type === 'game') {
        if (!openCasinoGameSession) {
          throw new Error('openCasinoGameSession is required for game links');
        }

        return executeLinkAction({
          action: args.action,
          openCasinoGameSession,
          demoOverride: args.demoOverride,
          isMobile: args.isMobile ?? defaultIsMobile ?? isMobile,
          language: args.language ?? defaultLanguage ?? getCurrentLanguage(),
          game: args.game,
          signal: args.signal,
        });
      }

      if (args.action.type === 'provider') {
        return executeLinkAction({
          action: args.action,
          navigateToCasinoLobby,
          signal: args.signal,
        });
      }

      return executeLinkAction({
        action: args.action,
        navigateToCasinoLobby,
        signal: args.signal,
      });
    },
    [
      executeLinkAction,
      defaultOpenCasinoGameSession,
      defaultNavigateToCasinoLobby,
      defaultIsMobile,
      defaultLanguage,
    ]
  );
}
